/**
 * @name MenuUser
 * @description Menú de acciones de usuario (Settings, Onboarding, Logout, Language)
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import styled from 'styled-components';

import { Environment } from '@config';
import { AuthService, EmployeeService, ResponseHandlerService, SnackbarService } from '@services';
import { Avatar, Icons, MUI, Typography } from '@styles';

// Material-UI
import MuiPopover from '@material-ui/core/Popover';

class MenuUser extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      anchorElLang: null,
      user: { name: null, job: null, img: null },
      isExternal: props.isExternal,
      history: props.history
    };
  }

  componentDidMount() {
    const { isExternal, user } = this.state;
    const employee = JSON.parse(localStorage.getItem('ls.infoUsuario')).Empleado;

    if (isExternal) {
      // Usuario externo
      user.name = employee.Nombre;
      user.job = i18n.t('GenericMessages.userExternal');
      user.img = employee.Foto || 'https://assets.talentry.io/icons/profile/TAL_Avatar.png';
    } else {
      // Usuario interno
      user.name = `${employee.Nombres} ${employee.Apellidos}`;
      user.job = employee.Puesto;
      user.img = employee.Foto.length > 0 ? employee.Foto : 'https://assets.talentry.io/icons/profile/TAL_Avatar.png';
    }

    this.setState({ user });
  }

  openHandler = (event) => {
    this.setState({
      anchorEl: event.currentTarget,
      anchorElLang: null
    });
  }

  closeHandler = () => {
    this.setState({
      anchorEl: null,
      anchorElLang: null
    });
  }

  openHandlerLang = (event) => {
    this.setState({ anchorElLang: event.currentTarget });
  }

  closeHandlerLang = () => {
    this.setState({ anchorElLang: null });
  }

  goToSettings = () => {
    let URL = Environment.getCurrent().domain;
    window.location.href = `${URL.substring(0, URL.length - 2)}talentry/#/configuraperfil`;
  }

  logout = () => {
    AuthService.clearStorage();
    AuthService.logout();
  }
  /**
   * Cambiar el lenguaje de la plataforma
   * @param {string} lang (es, en, pt)
   */
  changeLanguage = (lang) => {
    EmployeeService.putProfile({ language: lang })
      .then(() => {
        // Switch Language
        localStorage.setItem('ls.ty-Language', JSON.stringify({ language: lang }));
        i18n.use(initReactI18next).init({ lng: lang });

        EmployeeService.getInfo()
          .then((response) => {
            localStorage.removeItem('ls.infoUsuario');
            localStorage.setItem('ls.infoUsuario', JSON.stringify(response));
          });

        SnackbarService.success(i18n.t('GenericMessages.languageUpdated'));
        this.setState({ anchorElLang: null });
        window.location.reload();
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }

  render() {
    const {
      user,
      anchorEl,
      anchorElLang,
      isExternal,
      history
    } = this.state;

    return (
      <>
        <Styled.BoxTopbarIcon onClick={this.openHandler}>
          <MUI.Tooltip title={i18n.t('Topbar.userMenu')}>
            <Styled.BoxAvatar>
              <Avatar
                size="xs"
                src={user.img}
              />
              <Icons.ArrowDown />
            </Styled.BoxAvatar>
          </MUI.Tooltip>
        </Styled.BoxTopbarIcon>

        <Styled.Popover
          open={Boolean(anchorEl)}
          onClose={this.closeHandler}
          anchorEl={anchorEl}
        >
          <Styled.MenuHeader>
            <Styled.BoxHeader>
              <Styled.NameText>{user.name}</Styled.NameText>
              <Styled.JobText>
                <Typography.Small>{user.job}</Typography.Small>
              </Styled.JobText>
            </Styled.BoxHeader>
          </Styled.MenuHeader>

          <Styled.BoxUserActions>
            { /* Configuración */}
            {!isExternal && (
              <Styled.MenuItem onClick={this.goToSettings}>
                <Styled.BoxIcon>
                  <Icons.Settings />
                </Styled.BoxIcon>
                <Typography.Text>{i18n.t('Topbar.setting')}</Typography.Text>
              </Styled.MenuItem>
            )}

            {/* Onboarding */}
            {/* !isExternal && ( 
              <MenuItem>
                  {i18n.t('Topbar.onboarding')}
              </MenuItem>
            )*/}

            {/* Logout */}
            <Styled.MenuItem onClick={() => this.logout(history)}>
              <Styled.BoxIcon>
                <Icons.ExitToApp />
              </Styled.BoxIcon>
              <Typography.Text>{i18n.t('Topbar.logout')}</Typography.Text>
            </Styled.MenuItem>

            {/* Idioma */}
            <Styled.MenuItem onClick={this.openHandlerLang}>
              <Styled.BoxIcon>
                <Icons.Translate />
              </Styled.BoxIcon>
              <Typography.Text>{i18n.t('Topbar.language')}</Typography.Text>
              <MUI.Box marginLeft="8px">
                <Styled.BoxIcon>
                  <Icons.ArrowDown />
                </Styled.BoxIcon>
              </MUI.Box>
            </Styled.MenuItem>
          </Styled.BoxUserActions>
        </Styled.Popover>

        <Styled.PopoverLang
          open={Boolean(anchorElLang)}
          onClose={this.closeHandlerLang}
          anchorEl={anchorElLang}
          isExternal={isExternal}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Styled.MenuItem onClick={() => this.changeLanguage('es')}>
            <Typography.Text>{i18n.t('GenericMessages.es')}</Typography.Text>
          </Styled.MenuItem>
          <Styled.MenuItem onClick={() => this.changeLanguage('en')}>
            <Typography.Text>{i18n.t('GenericMessages.en')}</Typography.Text>
          </Styled.MenuItem>
          <Styled.MenuItem onClick={() => this.changeLanguage('pt')}>
            <Typography.Text>{i18n.t('GenericMessages.pt')}</Typography.Text>
          </Styled.MenuItem>
        </Styled.PopoverLang>
      </>
    );
  }
}

MenuUser.propTypes = {
  // Boolean que indica si es usuario externo.
  isExternal: PropTypes.bool.isRequired,
  // History router.
  history: PropTypes.object,
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxTopbarIcon = styled(MUI.ButtonText)`
  &.MuiButton-root{
    display: flex;
    width: auto;
    height: 60px;
    padding: 8px 12px;
    margin: 0;

    &:hover {
      text-decoration: none;
      background-color: ${(props) => props.theme.colors.greyHover};
      border-radius: 0%;
    }
  }
`;

Styled.BoxAvatar = styled.div`
  display: flex;
  align-items: center;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

Styled.NameText = styled(Typography.Text)`
  line-height: 1.2;
`;

Styled.JobText = styled(Typography.Text)`
  line-height: 1.2;
  color: ${(props) => props.theme.colors.greyDarkAlpha};
`;

Styled.BoxHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

Styled.BoxUserActions = styled.div`
  max-height: 470px;
  overflow-y: auto;
  scrollbar-width: thin !important; /* Scrollbar Firefox */
`;

Styled.BoxIcon = styled.div`
  display: flex;
  margin-right: 12px;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

// *************************************
// GLOBAL STYLED COMPONENTS          
// *************************************

Styled.Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    min-width: 270px;
    max-height: 520px;
    max-width: 608px;
  }
`;

Styled.PopoverLang = styled(MuiPopover)`
  .MuiPopover-paper {
    max-height: 520px;
    max-width: 608px;
    min-width: 220px;

    /*left: auto !important;
    right: 16px !important;
    top: ${(props) => !props.isExternal ? 270 : 220}px !important;*/
  }
`;

Styled.MenuItem = styled(MUI.MenuItem)`
  &.MuiMenuItem-root {
    height: 50px;
    padding: 0 16px 0 16px;
    &:hover {
      background-color: ${(props) => props.theme.colors.greyHover};
    }
    &:last-child {
      border-bottom: none;
    }
  }
`;

Styled.MenuHeader = styled(MUI.MenuHeader)``;

export default MenuUser;